import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import equal from 'deep-equal';

import {
  schemaSpouseBirthDate,
  schemaTrusteeFullname,
} from 'components/modules/Application/validation';
import { VOID_FUNCTION } from 'constants';
import { NAMES } from './TrusteesForm.constants';
import { KEY_COMPONENTS } from 'components/modules/MainAnketa/MainAnketa.constants';
import { MOBILE_PHONE_DEFAULT_VALUE } from '../PersonalForm/PersonalForm.constants';

export const useTrusteesFormPresenter = ({
  validate,
  errors,
  spouseForm,
  setFocusAt,
  onChangeSpouse,
  onChange,
}) => {
  const { persons, currentPersonId } = useSelector((state) => state.anketa);
  const { updateTrustees } = useSelector((state) => state.anketa.persons[currentPersonId]);
  const persona = persons[currentPersonId];

  const [localErrors, setLocalErrors] = useState({});
  const [initState, setInitState] = useState(true);
  const [trusteeId, setTrusteeId] = useState(null);
  const [trusteesFormValues, setTrusteesFormValues] = useState(null);
  const [spouseFormValues, setSpouseFormValues] = useState(null);
  const [showPhoneInfo, setShowPhoneInfo] = useState(false);
  const [showBirthdayInfo, setShowBirthdayInfo] = useState(false);

  const changeHandler = useCallback(
    (formValue) => {
      setTrusteesFormValues((previousFormValues) => {
        const mergedValues = Array.isArray(formValue)
          ? formValue.reduce((acc, value) => ({ ...acc, ...value }), previousFormValues)
          : { ...previousFormValues, ...formValue };

        onChange({
          values: mergedValues,
          id: trusteeId,
        });

        return mergedValues;
      });
    },
    [trusteeId, onChange]
  );

  const changeHandlerSpouse = useCallback(
    (formValue) => {
      setSpouseFormValues((previousFormValues) => {
        const mergedValues = Array.isArray(formValue)
          ? formValue.reduce((acc, value) => ({ ...acc, ...value }), previousFormValues)
          : { ...previousFormValues, ...formValue };

        onChangeSpouse({
          values: mergedValues,
          key: KEY_COMPONENTS.SPOUSE,
        });

        return mergedValues;
      });
    },
    [onChangeSpouse]
  );

  const changeHandlerTrusteeFio = useCallback(
    (formValue) => {
      if (formValue.fromDadata) {
        schemaTrusteeFullname
          .validate(formValue, { abortEarly: false })
          .then(() => {
            setFocusAt(`${KEY_COMPONENTS.TRUSTEES}_${trusteeId}_${NAMES.TRUSTEES_PHONE}_input`);
          })
          .catch(VOID_FUNCTION);

        delete formValue.fromDadata;
      }
      changeHandler(formValue);
    },
    [setFocusAt, changeHandler]
  );

  const changeHandlerTrusteePhone = useCallback(
    (formValue) => {
      const normalizedValue = formValue[NAMES.TRUSTEES_PHONE];

      return normalizedValue === MOBILE_PHONE_DEFAULT_VALUE
        ? changeHandler({ [NAMES.TRUSTEES_PHONE]: '' })
        : changeHandler({ [NAMES.TRUSTEES_PHONE]: normalizedValue });
    },
    [changeHandler]
  );

  const changeHandlerSpouseFio = useCallback(
    (formValue) => {
      if (formValue.fromDadata) {
        schemaTrusteeFullname
          .validate(formValue, { abortEarly: false })
          .then(() => {
            setFocusAt(`${KEY_COMPONENTS.SPOUSE}_${NAMES.SPOUSE_BIRTHDAY_DATE}_input`);
          })
          .catch(VOID_FUNCTION);

        delete formValue.fromDadata;
      }
      changeHandlerSpouse(formValue);
    },
    [setFocusAt, changeHandlerSpouse]
  );

  const changeHandlerSpouseBirthDate = useCallback(
    (formValue) => {
      const normalizedValue =
        formValue[NAMES.SPOUSE_BIRTHDAY_DATE]?.length === 2
          ? ''
          : formValue[NAMES.SPOUSE_BIRTHDAY_DATE];

      changeHandlerSpouse({ [NAMES.SPOUSE_BIRTHDAY_DATE]: normalizedValue });
    },
    [changeHandlerSpouse]
  );

  const changeHandlerBirthDateComplete = useCallback(
    (formValue) => {
      schemaSpouseBirthDate
        .validate(formValue, { abortEarly: false })
        .then(() => {
          setFocusAt(`${KEY_COMPONENTS.SPOUSE}_${NAMES.SPOUSE_PHONE}_input`);
        })
        .catch(VOID_FUNCTION);
    },
    [setFocusAt]
  );

  const changeHandlerSpousePhone = useCallback(
    (formValue) => {
      const normalizedValue = formValue[NAMES.SPOUSE_PHONE];

      return normalizedValue === MOBILE_PHONE_DEFAULT_VALUE
        ? changeHandlerSpouse({ [NAMES.SPOUSE_PHONE]: '' })
        : changeHandlerSpouse({ [NAMES.SPOUSE_PHONE]: normalizedValue });
    },
    [changeHandlerSpouse]
  );

  const validation = useCallback(
    (path) => {
      setLocalErrors((prev) => ({ ...prev, [path]: errors[path] || null }));
    },
    [errors]
  );

  const getError = useCallback(
    (path) => {
      const validationError = errors[path];
      const generalError = localErrors[path];

      if (!validate) {
        return generalError;
      }

      return validationError ?? generalError;
    },
    [validate, errors, localErrors]
  );

  useEffect(() => {
    if (
      !persona ||
      !persona.trustees ||
      Object.keys(persona.trustees).length === 0 ||
      equal(trusteesFormValues, persona.trustees[Object.keys(persona.trustees)[0]])
    ) {
      return;
    }

    setTrusteeId(Object.keys(persona.trustees)[0]);
    setTrusteesFormValues(persona.trustees[Object.keys(persona.trustees)[0]]);
  }, [persona, updateTrustees]);

  useEffect(() => {
    if (!persona || equal(spouseFormValues, persona[KEY_COMPONENTS.SPOUSE])) {
      return;
    }

    setSpouseFormValues(persona[KEY_COMPONENTS.SPOUSE]);
  }, [persona, updateTrustees]);

  useEffect(() => {
    if (spouseForm || !persona || !trusteesFormValues || !trusteesFormValues?.phone) {
      return;
    }

    setShowPhoneInfo(trusteesFormValues.phone === persona.contacts.mobilePhone);
  }, [persona, trusteesFormValues, spouseForm]);

  useEffect(() => {
    if (!spouseForm || !persona || !spouseFormValues?.phone) {
      return;
    }

    setShowPhoneInfo(spouseFormValues.phone === persona.contacts.mobilePhone);
  }, [persona, spouseFormValues, spouseForm]);

  useEffect(() => {
    if (!spouseForm || !persona || !spouseFormValues) {
      return;
    }

    setShowBirthdayInfo(spouseFormValues.birthDate.length === 0);
  }, [persona, spouseFormValues, spouseForm]);

  useEffect(() => {
    if (validate) {
      return;
    }

    setLocalErrors(
      Object.keys(localErrors).reduce((obj, key) => ({ ...obj, [key]: errors[key] || null }), {})
    );
  }, [errors, validate]);

  useEffect(() => {
    if (initState) {
      setInitState(false);
    } else {
      const cleanData = {
        phone: '',
        firstName: '',
        lastName: '',
        middleName: '',
        fullName: '',
        birthDate: '',
      };
      changeHandler(cleanData);
      changeHandlerSpouse(cleanData);
      validation(`${KEY_COMPONENTS.TRUSTEES}[0].${NAMES.TRUSTEES_FULL_NAME}`);
      validation(`${KEY_COMPONENTS.TRUSTEES}[0].${NAMES.TRUSTEES_PHONE}`);
      validation(`${KEY_COMPONENTS.SPOUSE}.${NAMES.SPOUSE_FULL_NAME}`);
      validation(`${KEY_COMPONENTS.SPOUSE}.${NAMES.SPOUSE_PHONE}`);
    }
  }, [spouseForm]);

  return {
    trusteeId,
    showPhoneInfo,
    showBirthdayInfo,
    spouseFormValues,
    trusteesFormValues,
    getError,
    validation,
    changeHandlerSpouseFio,
    changeHandlerTrusteeFio,
    changeHandlerSpousePhone,
    changeHandlerTrusteePhone,
    changeHandlerSpouseBirthDate,
    changeHandlerBirthDateComplete,
  };
};
